import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export const REQUEST_TYPE = "CO";
export const DOWNLOAD_TYPE = "CMPH";

export const ACHC_STATUS_OPTIONS = [
  { key: "", labelKey: "all", label: "All" },
  { key: "T", labelKey: "pending", label: "Pending" },
  { key: "A", labelKey: "approved", label: "Approved" },
  { key: "R", labelKey: "rejected", label: "Rejected" },
  { key: "D", labelKey: "processing_complete", label: "Processing Complete" },
];

const STATUS_MAP: Record<string, string> = {
  T: "pending",
  A: "approved",
  R: "rejected",
  D: "processing_complete",
};

export const FACILITY_TO_API_MAP: Record<string, string> = {
  SELF: "E",
  SPOUSE: "S",
  BOTH: "B",
};

export const FACILITY_OPTIONS = [
  { id: "SELF", label: "Self" },
  { id: "SPOUSE", label: "Spouse" },
  { id: "BOTH", label: "Both" },
];

export const MARITAL_STATUS_OPTIONS = [
  { id: "M", label: "Married" },
  { id: "U", label: "Unmarried / Single" },
  { id: "D", label: "Divorced" },
  { id: "W", label: "Widowed" },
];

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(
    user?.empId || user?.details?.empId || user?.usrid || user?.emp_id || ""
  ).trim();
}

export function getEmployeeDisplayName(user = getLoggedInUser()): string {
  const details = user?.details || {};
  return (
    [details?.firstname, details?.lsastname || details?.secondname || details?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "-"
  );
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractResults(data: any): any[] {
  const root = unwrap(data);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.data?.items)) return inner.data.items;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractDropdownOptions(data: any): { id: string; label: string }[] {
  return extractResults(data)
    .map((d: any) => ({
      id: String(d.Value ?? d.value ?? d.id ?? ""),
      label: String(d.Ddtext ?? d.DdText ?? d.ddtext ?? d.label ?? ""),
    }))
    .filter((o) => o.id && o.label);
}

export function formatSapDate(value: any): string {
  if (value === undefined || value === null || value === "") return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  if (msMatch) {
    const date = new Date(parseInt(msMatch[1], 10));
    if (isNaN(date.getTime())) return str;
    return toDmy(date);
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = str.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  const date = new Date(str);
  if (!isNaN(date.getTime())) return toDmy(date);
  return str;
}

function toDmy(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function formatSapDateTime(value: any): string {
  if (!value) return "-";
  const str = String(value).trim();
  const msMatch = str.match(/\/Date\((\d+)\)\//);
  const date = msMatch ? new Date(parseInt(msMatch[1], 10)) : new Date(str);
  if (isNaN(date.getTime())) return str;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${dd}-${mm}-${date.getFullYear()} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

export function formatDateDMY(date: Date): string {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}.${m}.${date.getFullYear()}`;
}

export function normalizeDobToDMY(raw: any): string {
  if (raw === undefined || raw === null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const ms = s.match(/\/Date\((\d+)\)\//);
  if (ms) {
    const d = new Date(parseInt(ms[1], 10));
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const dmy = s.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/);
  if (dmy) return `${dmy[1]}.${dmy[2]}.${dmy[3]}`;
  return s;
}

export function toApiDateTime(ymd: string): string {
  if (!ymd) return "";
  return `${ymd}T00:00:00`;
}

export function mapMaritalStatus(raw?: string): string {
  const v = (raw || "").trim().toLowerCase();
  if (!v) return "";
  if (v === "m" || v.startsWith("marr")) return "M";
  if (v === "u" || v.startsWith("sing") || v.startsWith("unm")) return "U";
  if (v === "d" || v.startsWith("div")) return "D";
  if (v === "w" || v.startsWith("wid")) return "W";
  return "";
}

export function getMaritalLabel(code?: string): string {
  const found = MARITAL_STATUS_OPTIONS.find((o) => o.id === String(code || "").toUpperCase());
  return found?.label || code || "-";
}

export function facilityLabel(code?: string): string {
  const v = String(code || "").toUpperCase();
  if (v === "E" || v === "SELF") return "Self (Employee)";
  if (v === "S" || v === "SPOUSE") return "Spouse";
  if (v === "B" || v === "BOTH") return "Both";
  return code || "-";
}

function isSpouseRelation(raw: any): boolean {
  const v = String(raw || "").trim().toLowerCase();
  if (!v) return false;
  return (
    v === "spouse" ||
    v === "wife" ||
    v === "husband" ||
    v.includes("spouse") ||
    v.includes("wife") ||
    v.includes("husband")
  );
}

function collectFamilyMembers(obj: any): any[] {
  if (!obj) return [];
  const candidates = [
    obj.familyMembers,
    obj.family_members,
    obj.FamilyMembers,
    obj.details?.familyMembers,
    obj.details?.family_members,
    obj.data?.familyMembers,
    obj.data?.data?.familyMembers,
  ];
  for (const list of candidates) {
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
}

function spouseFromMembers(members: any[]): { spouseName: string; spouseDob: string } {
  if (!Array.isArray(members) || members.length === 0) {
    return { spouseName: "", spouseDob: "" };
  }
  const spouse = members.find((m: any) =>
    isSpouseRelation(
      m?.relation ?? m?.Relation ?? m?.RelationToEmployee ?? m?.relationToEmployee ?? m?.relationship
    )
  );
  if (!spouse) return { spouseName: "", spouseDob: "" };
  const name = String(
    spouse.PatientName ??
      spouse.patientName ??
      spouse.name ??
      `${spouse.firstname || spouse.firstName || spouse.FirstName || ""} ${
        spouse.lastname || spouse.lastName || spouse.LastName || ""
      }`.trim()
  ).trim();
  const dob = normalizeDobToDMY(
    spouse.PatientDOB ??
      spouse.patientDob ??
      spouse.DOB ??
      spouse.dob ??
      spouse.birthDate ??
      spouse.birthdate ??
      spouse.BirthDate ??
      ""
  );
  return { spouseName: name, spouseDob: dob };
}

export function getSpouseFromProfile(user = getLoggedInUser()): { spouseName: string; spouseDob: string } {
  return spouseFromMembers(collectFamilyMembers(user));
}

export async function fetchSpouseDetails(): Promise<{ spouseName: string; spouseDob: string }> {
  const fromLocal = getSpouseFromProfile();
  if (fromLocal.spouseName) return fromLocal;

  try {
    const res: any = await Api.post(API_URLS.EMPLOYEES.My_PROFILE, {});
    const root = unwrap(res);
    const profile = root?.data?.data ?? root?.data ?? root;
    const fromProfile = spouseFromMembers(collectFamilyMembers(profile));
    if (fromProfile.spouseName) return fromProfile;
  } catch {
    /* fall through to patient-names */
  }

  try {
    const empId = getLoggedInEmpId();
    if (!empId) return { spouseName: "", spouseDob: "" };
    const padded = empId.padStart(8, "0");
    const res: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.PATIENT_NAMES, {
      empId: padded,
    });
    const fromPatients = spouseFromMembers(extractResults(res));
    if (fromPatients.spouseName) return fromPatients;
    if (padded !== empId) {
      const resRaw: any = await Api.post(API_URLS.MEDICAL_ATTENDANCE.PATIENT_NAMES, {
        empId,
      });
      return spouseFromMembers(extractResults(resRaw));
    }
  } catch {
    /* ignore */
  }

  return { spouseName: "", spouseDob: "" };
}

export function getStatusLabel(raw: any, t: (key: string) => string): string {
  const code = String(raw ?? "").trim().toUpperCase();
  const found = ACHC_STATUS_OPTIONS.find((opt) => opt.key === code);
  if (found) {
    const translated = t(found.labelKey);
    return translated === found.labelKey ? found.label : translated;
  }
  const mapped = STATUS_MAP[code] || String(raw ?? "").trim();
  if (!mapped) return "-";
  const translated = t(mapped);
  return translated === mapped ? mapped : translated;
}

export function getStatusBadgeClass(raw: any): string {
  const code = String(raw ?? "").trim().toUpperCase();
  if (code === "A") return "badge bg-success";
  if (code === "R") return "badge bg-danger";
  if (code === "T" || code === "PENDING") return "badge bg-warning text-dark";
  if (code === "D") return "badge bg-info text-dark";
  return "badge bg-secondary";
}

export function getRemarksList(detail: any): any[] {
  const fromHrms = Array.isArray(detail?.remarks_data) ? detail.remarks_data : [];
  const fromSap = Array.isArray(detail?.ToRemarks?.results)
    ? detail.ToRemarks.results
    : Array.isArray(detail?.ToRemarks)
      ? detail.ToRemarks
      : Array.isArray(detail?.RemarksHistory)
        ? detail.RemarksHistory
        : [];
  return [...fromHrms, ...fromSap];
}

export function getDocuments(detail: any): any[] {
  const docs = detail?.ToDocument?.results || detail?.documents || [];
  return Array.isArray(docs) ? docs : [];
}

export function mapApiItemToCard(apiItem: any) {
  const refNum =
    apiItem.ReferenceNumber && String(apiItem.ReferenceNumber).trim()
      ? String(apiItem.ReferenceNumber).trim()
      : "";
  const created = apiItem.RequestCreationDate
    ? String(apiItem.RequestCreationDate).trim()
    : apiItem.created_at
      ? formatSapDate(String(apiItem.created_at))
      : "";

  return {
    id: String(apiItem.id ?? refNum),
    referenceNumber: refNum,
    hospitalName: apiItem.HospitalName ?? "-",
    comprehensiveCheckupFor: apiItem.ComprehensiveCheckupFor ?? "",
    maritalStatus: apiItem.MaritalStatus ?? "",
    requestCreationDate: created,
    requestStatus: apiItem.RequestStatus ?? "",
    empId: apiItem.EmpID ?? "",
    raw: apiItem,
  };
}

export async function findAllAchc(payload: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<any[]> {
  const body: any = {
    page: payload.page ?? 1,
    limit: payload.limit ?? 100,
    search: payload.search ?? "",
  };
  if (payload.status) body.status = payload.status;
  if (payload.fromDate) body.fromDate = toApiDateTime(payload.fromDate);
  if (payload.toDate) body.toDate = toApiDateTime(payload.toDate);

  const res: any = await Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.FIND_ALL, body);
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load requests");
  }
  return extractResults(res);
}

export async function findOneAchc(id: string): Promise<any> {
  const res: any = await Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.FIND_ONE, { id });
  if (res?.success === false) {
    throw new Error(res?.message || "Failed to load details");
  }
  const root = unwrap(res);
  return root?.data?.data ?? root?.data ?? root;
}

export async function fetchAchcHospitals(): Promise<{ id: string; label: string }[]> {
  const res: any = await Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.HOSPITALS, {
    requestType: REQUEST_TYPE,
  });
  return extractDropdownOptions(res);
}

export async function createAchcRequest(payload: any): Promise<any> {
  return Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.CREATE_REQUEST, payload);
}

/** HTTP 201/200 create responses may omit `success: true` and only send a message. */
export function isAchcSubmitSuccess(res: any): boolean {
  if (!res) return false;
  const nested = res?.data;
  if (res.success === false) return false;
  if (nested && typeof nested === "object" && !Array.isArray(nested) && nested.success === false) {
    return false;
  }
  const status = Number(res.status ?? res.statusCode ?? nested?.status ?? 0);
  if (status >= 200 && status < 300) return true;
  if (res.success === true || nested?.success === true) return true;
  const msg = String(res.message || nested?.message || "").toLowerCase();
  if (msg.includes("success")) return true;
  return true;
}

export async function syncAchc(empId: string): Promise<any> {
  return Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.SYNC, { empId });
}

export type AchcDownloadDoc = {
  url: string;
  fileName: string;
  content?: string;
  mimeType: string;
};

function pickDocUrl(obj: any): string {
  if (!obj || typeof obj !== "object") return "";
  return String(
    obj.url ||
      obj.Url ||
      obj.URL ||
      obj.fileUrl ||
      obj.FileUrl ||
      obj.file_url ||
      obj.signedUrl ||
      obj.signed_url ||
      obj.downloadUrl ||
      obj.s3Url ||
      obj.attachmentUrl ||
      obj.serverURL ||
      ""
  ).trim();
}

function pickDocContent(obj: any): string {
  if (!obj || typeof obj !== "object") return "";
  return String(
    obj.Content || obj.content || obj.FileContent || obj.fileContent || obj.file || ""
  ).trim();
}

/**
 * Same unwrap as the mobile `apiHelper` + Viewcheckup:
 *   1) if data.status is 200/201 (or data.success) → use data.data
 *   2) else if data.data.status is 200/201 → use data.data.data
 *   3) then take data.data[0] / data[0]
 */
function extractAchcDownloadDoc(res: any): any {
  if (!res) return null;

  let payload: any = res;
  if (
    payload?.data?.status === 200 ||
    payload?.data?.status === 201 ||
    payload?.data?.success
  ) {
    payload = payload.data.data;
  } else if (
    payload?.data?.data?.status === 200 ||
    payload?.data?.data?.status === 201
  ) {
    payload = payload.data.data.data;
  } else {
    payload = payload?.data ?? payload;
  }

  if (Array.isArray(payload) && payload.length) return payload[0];
  if (Array.isArray(payload?.data) && payload.data.length) return payload.data[0];
  if (Array.isArray(payload?.data?.data) && payload.data.data.length) {
    return payload.data.data[0];
  }
  if (payload && typeof payload === "object" && (pickDocUrl(payload) || pickDocContent(payload))) {
    return payload;
  }
  return payload;
}

function achcDownloadLooksSuccessful(res: any): boolean {
  const codes = [res?.status, res?.statusCode, res?.data?.status, res?.data?.data?.status]
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n) && n > 0);
  if (codes.some((n) => n >= 200 && n < 300)) return true;
  if (res?.success === true || res?.data?.success === true) return true;
  const msg = String(res?.message || res?.data?.message || "").toLowerCase();
  return msg.includes("success") || msg.includes("fetched");
}

function base64ToPdfObjectUrl(base64: string): string {
  let cleaned = base64.trim();
  const commaIdx = cleaned.indexOf("base64,");
  if (commaIdx !== -1) cleaned = cleaned.slice(commaIdx + "base64,".length);
  cleaned = cleaned.replace(/\s/g, "");
  const byteChars = atob(cleaned);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

/** Open the letter in a new browser tab (web equivalent of the app viewer). */
export function openAchcDocumentInNewTab(
  doc: AchcDownloadDoc,
  previewWindow?: Window | null
): void {
  let viewUrl = doc.url;
  if (!viewUrl && doc.content) {
    viewUrl = /^https?:\/\//i.test(doc.content)
      ? doc.content
      : base64ToPdfObjectUrl(doc.content);
  }
  if (!viewUrl) {
    throw new Error("Failed to download letter");
  }

  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.replace(viewUrl);
    previewWindow.focus();
    return;
  }

  const opened = window.open(viewUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    const a = document.createElement("a");
    a.href = viewUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

export async function downloadAchcLetter(
  empId: string,
  referenceNumber: string
): Promise<AchcDownloadDoc> {
  const payload = {
    empId: String(empId || "").trim(),
    type: DOWNLOAD_TYPE,
    referenceNumber: String(referenceNumber || "").trim(),
  };

  let res: any;
  try {
    res = await Api.post(API_URLS.COMPREHENSIVE_HEALTH_CHECK.DOWNLOAD, payload);
  } catch (err: any) {
    // Some gateways surface HTTP 201 as an Axios error even though the body is success.
    const status = Number(err?.response?.status || 0);
    if (status === 200 || status === 201) {
      res = err.response?.data;
    } else {
      throw err;
    }
  }

  const docItem = extractAchcDownloadDoc(res);
  const rawUrl = pickDocUrl(docItem) || pickDocUrl(res);
  const content = pickDocContent(docItem);
  const fileName =
    String(docItem?.FileName || docItem?.fileName || "").trim() ||
    `Health_Checkup_Letter_${payload.referenceNumber || "document"}.pdf`;
  const mimeType = String(
    docItem?.FileType || docItem?.fileType || docItem?.mimeType || "application/pdf"
  );

  const url = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : /^https?:\/\//i.test(content)
      ? content
      : "";

  if (!url && !content) {
    throw new Error(
      achcDownloadLooksSuccessful(res)
        ? "Document URL not found. Please try again."
        : res?.message || res?.data?.message || "Failed to download letter"
    );
  }

  return { url, fileName, content: url ? undefined : content, mimeType };
}
